// v3.15-977 — Bill.com sync status badge + manual Retry button.
// Reads /api/clients/{id}/bill-sync/status and renders one of:
//   - synced (green) with the Bill customer id
//   - pending (slate) — sync in flight from creation
//   - failed (rose) with a Retry button that calls
//     POST /api/clients/{id}/bill-sync/retry (always runs, ignores cap)
//   - not_configured (slate ghost) — Bill.com creds missing in env;
//     admin should add them before retry has any effect.
import { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';

const BADGE = {
  synced:        { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Synced',   Icon: CheckCircle2 },
  pending:       { color: 'bg-slate-100 text-slate-700 border-slate-200',       label: 'Pending',  Icon: Clock },
  failed:        { color: 'bg-rose-100 text-rose-800 border-rose-200',          label: 'Failed',   Icon: XCircle },
  not_configured:{ color: 'bg-slate-100 text-slate-500 border-slate-200',       label: 'Not Configured', Icon: AlertCircle },
};

export const BillSyncBadge = ({ clientId }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await api.get(`/clients/${clientId}/bill-sync/status`);
      setState(data);
    } catch {
      setState({ status: 'not_configured' });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/bill-sync/retry`);
      if (data?.status === 'synced') {
        toast.success('Bill.com sync recovered');
      } else if (data?.status === 'not_configured') {
        toast.message(data.message || 'Bill.com is not configured');
      } else {
        toast.error(`Bill sync still failing: ${data?.error || 'unknown'}`);
      }
      await refresh();
    } catch (e) {
      toast.error('Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-400" data-testid="bill-sync-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Bill.com…
      </span>
    );
  }

  const status = state?.status || 'not_configured';
  const meta = BADGE[status] || BADGE.not_configured;
  const { Icon, color, label } = meta;

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`bill-sync-${status}`}>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide ${color}`}>
        <Icon className="h-3 w-3" /> Bill.com: {label}
      </span>
      {state?.bill_customer_id && (
        <span className="text-[10px] text-slate-400 font-mono" title="Bill.com Customer ID" data-testid="bill-customer-id">
          #{state.bill_customer_id.slice(0, 12)}…
        </span>
      )}
      {status === 'failed' && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[10px] border-rose-200 text-rose-700 hover:bg-rose-50"
          onClick={handleRetry}
          disabled={retrying}
          data-testid="bill-sync-retry-btn"
        >
          {retrying ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Retry Bill Sync
        </Button>
      )}
    </div>
  );
};

export default BillSyncBadge;
